import React from "react";
import { Box, Typography } from "@mui/material";

const DogChasingBallLoader = ({ message = "Fetching DataDog alerts..." }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "200px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animation Container */}
      <Box
        sx={{
          position: "relative",
          width: "350px",
          height: "120px",
          marginBottom: 2,
        }}
      >
        {/* Ball - bounces ahead of the dog with smooth physics */}
        <Box
          sx={{
            position: "absolute",
            width: "18px",
            height: "18px",
            backgroundColor: "#ff6b35",
            borderRadius: "50%",
            boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
            animation: "ballBounce 5s linear infinite",
            "@keyframes ballBounce": {
              "0%": {
                left: "50px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "10%": {
                left: "80px",
                top: "35px",
                transform: "scale(0.9)",
                boxShadow: "0 8px 12px rgba(255, 107, 53, 0.3)",
              },
              "20%": {
                left: "120px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "30%": {
                left: "160px",
                top: "25px",
                transform: "scale(0.85)",
                boxShadow: "0 12px 18px rgba(255, 107, 53, 0.2)",
              },
              "40%": {
                left: "200px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "50%": {
                left: "240px",
                top: "30px",
                transform: "scale(0.9)",
                boxShadow: "0 10px 15px rgba(255, 107, 53, 0.3)",
              },
              "60%": {
                left: "280px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "70%": {
                left: "320px",
                top: "40px",
                transform: "scale(0.95)",
                boxShadow: "0 6px 10px rgba(255, 107, 53, 0.3)",
              },
              "75%": {
                left: "340px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "78%": {
                left: "340px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "82%": {
                left: "320px",
                top: "45px",
                transform: "scale(0.9)",
                boxShadow: "0 8px 12px rgba(255, 107, 53, 0.3)",
              },
              "86%": {
                left: "280px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "90%": {
                left: "220px",
                top: "40px",
                transform: "scale(0.9)",
                boxShadow: "0 8px 12px rgba(255, 107, 53, 0.3)",
              },
              "94%": {
                left: "160px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
              "97%": {
                left: "100px",
                top: "45px",
                transform: "scale(0.9)",
                boxShadow: "0 8px 12px rgba(255, 107, 53, 0.3)",
              },
              "100%": {
                left: "50px",
                top: "70px",
                transform: "scale(1)",
                boxShadow: "0 2px 6px rgba(255, 107, 53, 0.4)",
              },
            },
          }}
        />

        {/* Dog with realistic walking animation */}
        <Box
          sx={{
            position: "absolute",
            fontSize: "28px",
            top: "60px",
            animation: "dogChase 5s ease-in-out infinite, legMovement 0.3s ease-in-out infinite",
            transformOrigin: "center",
            "@keyframes dogChase": {
              "0%": {
                left: "0px",
                transform: "scaleX(-1) rotate(0deg)", // Face right
              },
              "5%": {
                left: "15px",
                transform: "scaleX(-1) rotate(-1deg)",
              },
              "10%": {
                left: "35px",
                transform: "scaleX(-1) rotate(1deg)",
              },
              "20%": {
                left: "70px",
                transform: "scaleX(-1) rotate(-2deg)",
              },
              "30%": {
                left: "110px",
                transform: "scaleX(-1) rotate(0deg)",
              },
              "40%": {
                left: "150px",
                transform: "scaleX(-1) rotate(2deg)",
              },
              "50%": {
                left: "190px",
                transform: "scaleX(-1) rotate(0deg)",
              },
              "60%": {
                left: "230px",
                transform: "scaleX(-1) rotate(-3deg)",
              },
              "70%": {
                left: "270px",
                transform: "scaleX(-1) rotate(0deg)",
              },
              "75%": {
                left: "290px",
                transform: "scaleX(-1) rotate(1deg)",
              },
              "78%": {
                left: "300px",
                transform: "scaleX(1) rotate(0deg)", // Turn around slowly
              },
              "82%": {
                left: "280px",
                transform: "scaleX(1) rotate(-1deg)", // Slow walk back
              },
              "86%": {
                left: "250px",
                transform: "scaleX(1) rotate(1deg)",
              },
              "90%": {
                left: "200px",
                transform: "scaleX(1) rotate(-1deg)",
              },
              "94%": {
                left: "140px",
                transform: "scaleX(1) rotate(0deg)",
              },
              "97%": {
                left: "70px",
                transform: "scaleX(1) rotate(1deg)",
              },
              "99%": {
                left: "20px",
                transform: "scaleX(1) rotate(-1deg)",
              },
              "100%": {
                left: "0px",
                transform: "scaleX(-1) rotate(0deg)", // Back to start, face right
              },
            },
            "@keyframes legMovement": {
              "0%": {
                filter: "brightness(1) saturate(1)",
                marginTop: "0px",
                textShadow: "0 0 3px rgba(0,0,0,0.3)",
              },
              "25%": {
                filter: "brightness(1.2) saturate(1.3)",
                marginTop: "-3px",
                textShadow: "1px 1px 4px rgba(0,0,0,0.4)",
              },
              "50%": {
                filter: "brightness(1) saturate(1)",
                marginTop: "0px",
                textShadow: "0 0 3px rgba(0,0,0,0.3)",
              },
              "75%": {
                filter: "brightness(1.2) saturate(1.3)",
                marginTop: "-2px",
                textShadow: "-1px 1px 4px rgba(0,0,0,0.4)",
              },
              "100%": {
                filter: "brightness(1) saturate(1)",
                marginTop: "0px",
                textShadow: "0 0 3px rgba(0,0,0,0.3)",
              },
            },
          }}
        >
          🐕
        </Box>

        {/* Paw prints trail - appears as dog runs smoothly */}
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              fontSize: "10px",
              color: "#888",
              top: "85px",
              animation: `pawPrint${i} 3s linear infinite`,
              [`@keyframes pawPrint${i}`]: {
                "0%": {
                  left: `${-15 + i * 5}px`,
                  opacity: 0,
                  transform: "scale(0.5)",
                },
                [`${(i + 1) * 8}%`]: {
                  left: `${15 + i * 45}px`,
                  opacity: 1,
                  transform: "scale(1)",
                },
                [`${(i + 3) * 8}%`]: {
                  left: `${25 + i * 45}px`,
                  opacity: 0.8,
                  transform: "scale(0.9)",
                },
                "85%": {
                  left: `${280 + i * 8}px`,
                  opacity: 0.2,
                  transform: "scale(0.7)",
                },
                "100%": {
                  left: `${-15 + i * 5}px`,
                  opacity: 0,
                  transform: "scale(0.5)",
                },
              },
            }}
          >
            🐾
          </Box>
        ))}

        {/* Dust clouds when ball bounces */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={`dust-${i}`}
            sx={{
              position: "absolute",
              fontSize: "14px",
              color: "#ddd",
              animation: `dustCloud${i} 1.8s ease-in-out infinite`,
              [`@keyframes dustCloud${i}`]: {
                "0%": {
                  left: `${30 + i * 140}px`,
                  top: "75px",
                  opacity: 0,
                  transform: "scale(0.3)",
                },
                [`${15 + i * 30}%`]: {
                  left: `${35 + i * 140}px`,
                  top: "75px",
                  opacity: 0.6,
                  transform: "scale(1)",
                },
                [`${25 + i * 30}%`]: {
                  left: `${40 + i * 140}px`,
                  top: "80px",
                  opacity: 0.3,
                  transform: "scale(1.2)",
                },
                [`${35 + i * 30}%`]: {
                  left: `${45 + i * 140}px`,
                  top: "85px",
                  opacity: 0,
                  transform: "scale(1.5)",
                },
                "100%": {
                  opacity: 0,
                },
              },
            }}
          >
            �
          </Box>
        ))}
      </Box>

      {/* Loading Text */}
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          animation: "textPulse 1.5s ease-in-out infinite alternate",
          "@keyframes textPulse": {
            "0%": {
              opacity: 0.7,
            },
            "100%": {
              opacity: 1,
            },
          },
        }}
      >
        {message}
      </Typography>

      {/* Animated dots */}
      <Box
        sx={{
          display: "flex",
          gap: "4px",
          marginTop: 1,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: "6px",
              height: "6px",
              backgroundColor: "primary.main",
              borderRadius: "50%",
              animation: `dotBounce 1.4s ease-in-out infinite both`,
              animationDelay: `${i * 0.16}s`,
              "@keyframes dotBounce": {
                "0%, 80%, 100%": {
                  transform: "scale(0)",
                },
                "40%": {
                  transform: "scale(1)",
                },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DogChasingBallLoader;
